import type { QueryKey } from '@tanstack/react-query';

import { ENDPOINTS, type SearchPackagesResponse } from '../../../../shared/registrySearch';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';

const STALE_MS = 60_000;
const SEARCH_LIMIT = 20;

const getQueryKey = (fetcher: ThirdPartyFetcher | undefined, query: string): QueryKey =>
  ['registry-search', 'packages', fetcher, query];

/**
 * Fetches package suggestions from the registry-search facade. Used to power the package-name
 * dropdown in the Add Dependency drawer.
 *
 * The full `query` (including any host prefix like `github.com/`) is sent on the wire so the
 * backend's per-impl `supports?` routing has the host context to pick the right
 * `RegistryServer`; impls strip their host prefix internally before forwarding to the upstream
 * registry's search endpoint. Caller is responsible for stripping prefixes for any UI-side
 * length-threshold checks.
 *
 * Disabled until `fetcher` and `query` are set AND the caller signals via `enabled`. The extra
 * gate lets callers skip the lookup entirely for inputs we structurally can't autocomplete
 * (e.g. a `gitlab.com/...` git locator while only GitHub has a `RegistryServer`) or that
 * haven't yet hit the UI's minimum-length threshold.
 */
export const useSearchPackages = (
  fetcher: ThirdPartyFetcher | undefined,
  query: string,
  enabled: boolean = true,
) => {
  // Trim at the fetch boundary (as useSearchPackageVersions does): a whitespace-only or
  // edge-padded query shouldn't fire a request the registry-search validator would 400 on
  // (SAFE_CHARS rejects spaces). Trimming only strips outer whitespace, so any host prefix the
  // backend's routing needs (e.g. `github.com/`) is preserved.
  const trimmedQuery = query.trim();

  return useQuery<SearchPackagesResponse>({
    queryKey: getQueryKey(fetcher, trimmedQuery),
    queryFn: () => get<SearchPackagesResponse>(
      ENDPOINTS.SEARCH_PACKAGES,
      false,
      {
        fetcher: fetcher ?? '',
        q: trimmedQuery,
        limit: SEARCH_LIMIT,
      },
    ),
    enabled: enabled && !!fetcher && !!trimmedQuery,
    staleTime: STALE_MS,
  });
};
